import { useState } from "react";
import { motion } from "framer-motion";
import {
  Edit,
  Trash2,
  UserX,
  UserCheck,
  Search,
  Filter,
  MoreHorizontal,
} from "lucide-react";
import {
  Button,
  Input,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DataTable,
} from "@b2b/ui-components";

import { SubAccount, SUB_ACCOUNT_ROLES } from "../types/subAccount";

interface SubAccountTableProps {
  subAccounts: SubAccount[];
  onEdit: (subAccount: SubAccount) => void;
  onSuspend: (id: string) => void;
  onActivate: (id: string) => void;
  onDelete: (id: string) => void;
}

export const SubAccountTable = ({
  subAccounts,
  onEdit,
  onSuspend,
  onActivate,
  onDelete,
}: SubAccountTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const getStatusBadge = (status: SubAccount["status"]) => {
    const statusStyles = {
      active: "bg-success/10 text-success border-success/20",
      suspended: "bg-destructive/10 text-destructive border-destructive/20",
      pending: "bg-warning/10 text-warning border-warning/20",
    };

    return (
      <Badge className={statusStyles[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getRoleBadge = (roleId: string) => {
    const role = SUB_ACCOUNT_ROLES.find((r) => r.id === roleId);
    const roleStyles: Record<string, string> = {
      admin: "bg-purple-500/10 text-purple-700 border-purple-200",
      sales: "bg-blue-500/10 text-blue-700 border-blue-200",
      support: "bg-green-500/10 text-green-700 border-green-200",
      finance: "bg-orange-500/10 text-orange-700 border-orange-200",
      logistics: "bg-cyan-500/10 text-cyan-700 border-cyan-200",
    };

    return (
      <Badge
        className={
          roleStyles[roleId as keyof typeof roleStyles] ||
          "bg-gray-500/10 text-gray-700 border-gray-200"
        }
      >
        {role?.name || roleId}
      </Badge>
    );
  };

  const filteredAccounts = subAccounts.filter((account) => {
    const matchesSearch =
      account.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === "all" || account.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" || account.status === statusFilter;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const transformedData = filteredAccounts.map((account) => ({
    id: account.id,
    username: account.username,
    email: account.email,
    role: getRoleBadge(account.role),
    status: getStatusBadge(account.status),
    actions: (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(account)}>
            <Edit className="mr-2 h-4 w-4" /> Edit
          </DropdownMenuItem>
          {account.status === "active" ? (
            <DropdownMenuItem onClick={() => onSuspend(account.id)}>
              <UserX className="mr-2 h-4 w-4" /> Suspend
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => onActivate(account.id)}>
              <UserCheck className="mr-2 h-4 w-4" /> Activate
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => onDelete(account.id)}>
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  }));
  const quotesTableConfig = {
    columns: [
      { key: "username", label: "Username" },
      { key: "email", label: "Email" },
      { key: "role", label: "Role" },
      { key: "status", label: "Status" },
      { key: "actions", label: "Actions" },
    ],
    styles: {
      headerBg: "bg-gray-100",
      headerText: "text-gray-800",
      rowHover: "bg-gray-50",
    }, // Provide required styles for StylesConfig type
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by username or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-32">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {SUB_ACCOUNT_ROLES.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-lg border bg-card shadow-card overflow-hidden"
      >
        <DataTable
          data={transformedData}
          config={quotesTableConfig}
          rowsPerPage={3}
        />

        {filteredAccounts.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <p className="text-lg font-medium">No sub-accounts found</p>
            <p className="text-sm">
              Try adjusting your search or filter criteria
            </p>
          </div>
        )}
      </motion.div>

      <div className="text-sm text-muted-foreground">
        Showing {filteredAccounts.length} of {subAccounts.length} sub-accounts
      </div>
    </div>
  );
};
